import { useCallback, useMemo } from "react";

import { MANAGE_PROXY_CONTACTS } from "Flocknote/GlobalComponents/Modal/contentKeys";
import { useModal } from "Flocknote/stateHooks";

import { useHouseholdData } from "HouseholdProfile/API/queryHooks";

import calculateAge from "Utilities/calculateAge";
import makeItAnArray from "Utilities/makeItAnArray";
import { useAddUrlParam, useRemoveUrlParams } from "Utilities/urlParamHooks";

import { useUserChipContext } from "./UserChip";

const useAge = () => {
  const { birthday } = useUserChipContext();

  return useMemo(() => calculateAge(birthday), [birthday]);
};

const useOtherMembers = () => {
  const { userID } = useUserChipContext();
  const { members = [] } = useHouseholdData();

  return useMemo(
    () => members.filter(({ userID: localID }) => localID !== userID),
    [userID, members]
  );
};

const useLoadProfile = () => {
  const { userID } = useUserChipContext();
  const removeUrlParams = useRemoveUrlParams();
  const addProfileTabToUrl = useAddProfileTabToUrl();

  return useCallback(() => {
    window.disablePersonCardByUserID(userID); // disables ability to click the person on the people page while the profile is loading or if it is open
    removeUrlParams("householdID");
    window.fnReactBus.loadUserProfile(userID);
    // Add the url param after loading the profile so that it's not removed by family tab code
    addProfileTabToUrl();
    // loadUserProfile also sets profileDataIsLoading to true, which disables the household member chips
    // profileDataIsLoading is then set to false by showProfile in the userProfileHandler once the profile is loaded
  }, [addProfileTabToUrl, removeUrlParams, userID]);
};

const useOpenProxyContacts = () => {
  const [, { openModal }] = useModal();
  const { userID } = useUserChipContext();

  return useCallback(
    () => openModal(MANAGE_PROXY_CONTACTS, { targetUserID: userID }),
    [openModal, userID]
  );
};

const useAddProfileTabToUrl = () => {
  const addUrlParam = useAddUrlParam();

  const storedTab = makeItAnArray(
    JSON.parse(localStorage.getItem("userprofile_tab"))
  );

  return useCallback(() => {
    addUrlParam("profileTab", storedTab[0] || "info");
  }, [addUrlParam, storedTab]);
};

export {
  useAge,
  useLoadProfile,
  useOpenProxyContacts,
  useOtherMembers,
  useAddProfileTabToUrl,
};
